import WithLayout from "@components/HOC/withLayout";
import Loader from "@components/global/Loader/Loader";
import { Panel } from "@components/global/new/Panel/Panel";
import { Search } from "@components/global/new/Search/Search";
import LocationRow3 from "@mobx/components/LocationRow3";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import rackTemplatesStore from "@mobx/store/locations/rackTemplate";
import { ILocation3 } from "@mobx/store/locations/types";
import unallocatedMinersStore from "@mobx/store/locations/unallocated";
import checkLocationsURL, { CheckMode } from "@mobx/utils/checkLocationsURL";
import AUTHSTATES from "@store/auth/AuthStates";
import { ROUTES_E } from "@utils/RoutsName";
import { useAuth } from "@utils/hooks/useAuth";
import { useAllUsersUpdater } from "@utils/hooks/useCurrentUser";
import { useMe } from "@utils/hooks/useMe";
import useMediaQuery from "@utils/hooks/utils";
import { Button } from "antd";
import cn from "classnames";
import { AnimatePresence, Reorder, useDragControls } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Locations.module.scss";
import { TextFieldWithCopy } from "@components/TextFieldWithCopy/TextFieldWithCopy";

interface LocationIDPair {
  location?: number;
  building?: number;
}

const useControl = () => {
  const auth = useAuth();
  const me = useMe();
  const navigate = useNavigate();
  const onPointerUpRef = useRef<() => void>();
  const [drag, setDrag] = useState(false);
  const allUsersUpdater = useAllUsersUpdater();
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  const [wantedPair, setWantedPair] = useState<LocationIDPair>();
  const firstRun = useRef(true);
  const [searchParams] = useSearchParams();
  const paramBuilding = searchParams.get("building");
  const paramLocation = searchParams.get("location");
  const [filter, setFilter] = useState("");

  useWatchNetwork(mainLocationsStore, locationStore, buildingStore);
  useEffect(() => {
    mainLocationsStore.init();
    rackTemplatesStore.init(auth.role);
    unallocatedMinersStore.init(auth.role);
    allUsersUpdater();
  }, []);

  useEffect(() => {
    if (!auth || auth.state === AUTHSTATES.UNAUTHORIZED) {
      navigate(ROUTES_E.LOGIN);
      return;
    }
  }, [auth]);

  useEffect(() => {
    if (!mainLocationsStore.isInitialized) return;
    checkLocationsURL(
      (l, b) => {
        const wanted: LocationIDPair = {
          location: l?.url,
          building: b?.url,
        };
        setWantedPair(wanted);
        if (wanted.location === undefined && wanted.building === undefined) mainLocationsStore.setActiveLocation(undefined);
      },
      CheckMode.Both,
      locationStore?.location.Id,
      buildingStore?.building.id
    );
  }, [paramBuilding, paramLocation, mainLocationsStore.isInitialized]);

  useEffect(() => {
    if (!mainLocationsStore.isInitialized) return;
    if (!wantedPair) return;
    if (wantedPair.location && wantedPair.location !== locationStore?.location.Id) {
      mainLocationsStore.setActiveLocation(wantedPair?.location);
      setWantedPair(p => ({ ...p, location: undefined }));
    }
  }, [wantedPair?.location, locationStore?.location.Id, mainLocationsStore.isInitialized]);

  useEffect(() => {
    if (!mainLocationsStore.isInitialized) return;
    if (!wantedPair || !locationStore?.location.Id) return;
    if (wantedPair.building && wantedPair.building !== buildingStore?.building.id) {
      mainLocationsStore.activeLocation?.setActiveBuilding(wantedPair?.building);
      setWantedPair(p => ({ ...p, building: undefined }));
    }
  }, [wantedPair, buildingStore?.building.id, locationStore?.location.Id, mainLocationsStore.isInitialized]);

  useEffect(() => {
    if (!mainLocationsStore.isInitialized) return;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    checkLocationsURL(
      (l, b) => {
        const params = new URLSearchParams(window.location.search);
        if (l) {
          if (l.given !== undefined) {
            params.set('location', l.given.toString());
          } else {
            params.delete('location');
          }
        }

        if (b) {
          if (b.given !== undefined) {
            params.set('building', b.given.toString());
          } else {
            params.delete('building');
          }
        }

        navigate(`?${params.toString()}`, { replace: true });
      },
      CheckMode.Both,
      mainLocationsStore.activeLocation?.location.Id,
      mainLocationsStore.activeLocation?.activeBuilding?.building.id
    );
  }, [buildingStore?.building.id, locationStore?.location.Id, navigate, mainLocationsStore.isInitialized]);

  useEffect(() => {
    mainLocationsStore.checkPositionsOfLocations(me?.email.value);
  }, [me]);

  const onEditLocationPosition = (l: number[]) => {
    if (l.length !== filteredLocations.length) return;
    mainLocationsStore.editPositionsOfLocations(l, me?.email.value);
  };

  useEffect(() => {
    if (drag) {
      const onPointerUp = () => {
        setDrag(false);
        if (onPointerUpRef.current)
          window.removeEventListener("pointerup", onPointerUpRef.current);
      };
      onPointerUpRef.current = onPointerUp;
      window.addEventListener("pointerup", onPointerUp);
      return () => {
        if (onPointerUpRef.current)
          window.removeEventListener("pointerup", onPointerUpRef.current);
      };
    }
  }, [drag]);

  useEffect(() => {
    const cb = (ev: Event) => {
      const evt = ev as CustomEvent;
      setDrag(evt.detail);
    };
    document.addEventListener("no-select", cb);
    return () => document.removeEventListener("no-select", cb);
  }, []);

  const locations = useMemo(
    () => mainLocationsStore.locationsInOrder.map(store => store.location),
    [mainLocationsStore.locationsInOrder, ...mainLocationsStore.locationsInOrder.map(s => s.location)],
  );

  const filteredLocations = useMemo(() => locations.filter((item: ILocation3) => {
    if (filter.trim()) {
      return item.Name.toLowerCase().includes(filter.trim().toLowerCase()) || String(item.Code).includes(filter.trim());
    } else {
      return true;
    }
  }), [locations, filter]);

  const hasActive = useMemo(() => mainLocationsStore.activeLocation !== undefined, [mainLocationsStore.activeLocation]);

  useEffect(() => {
    console.log("filteredLocations", filteredLocations);
  }, [filteredLocations]);

  return {
    isAdmin: auth.isAdmin,
    controllerId: auth.controllerId,
    me,
    locations,
    filteredLocations,
    drag,
    loading: mainLocationsStore.isLoading,
    initialized: mainLocationsStore.isInitialized,
    filter,
    hasActive,

    setFilter,
    showAddNewLocation: () => mainLocationsStore.showAddLocationModal(),
    onEditLocationPosition,
    onDrag(ev: React.PointerEvent) {
      setDrag(true);
      ev.currentTarget.setPointerCapture(ev.pointerId);
    },
  };
};

interface LocItemProps {
  loc: ILocation3,
  index: number,
  isPhone: boolean,
  hasActive: boolean,
  headers: string[],
  onDrag: (ev: React.PointerEvent) => void
}

const LocItem = (props: LocItemProps) => {
  const { loc, index, hasActive, isPhone, headers, onDrag } = props;
  const dragsCtrl = useDragControls();

  return <AnimatePresence key={loc.Id}>
    {
      hasActive
        ? <div key={loc.Id}>
          <LocationRow3
            index={index}
            isPhone={isPhone}
            headers={headers}
            location={{ ...loc }}
            onDrag={(ev) => {
              onDrag(ev);
              dragsCtrl.start(ev);
            }}
          />
        </div>
        : <Reorder.Item
          key={loc.Id}
          layout="position"
          style={{ position: 'relative', zIndex: 0 }} // ← важно!
          value={loc.Id}
          dragListener={false}
          dragControls={dragsCtrl}
          as='div'
          whileDrag={{
            boxShadow: "0px 2px 26px #deeafd",
            borderRadius: 17,
            background: "#fff",
            zIndex: 99999999,
          }}
        >
          <LocationRow3
            index={index}
            isPhone={isPhone}
            headers={headers}
            location={{ ...loc }}
            onDrag={(ev) => {
              onDrag(ev);
              dragsCtrl.start(ev);
            }}
          />
        </Reorder.Item>
    }
  </AnimatePresence>
}

const Locations3 = observer(() => {
  const { formatMessage } = useIntl();
  const control = useControl();
  const isPhone = useMediaQuery("max", 575);

  const headers = [
    { title: formatMessage({ id: "locations.table.name" }), isAdmin: false },
    { title: formatMessage({ id: "locations.table.id" }), isAdmin: false },
    { title: formatMessage({ id: "locations.table.address" }), isAdmin: false },
    { title: formatMessage({ id: "locations.table.appKey" }), isAdmin: true },
    { title: formatMessage({ id: "locations.table.coords" }), isAdmin: true },
    { title: formatMessage({ id: "locations.table.notes" }), isAdmin: true },
  ];

  return (
    <Panel
      className={cn(styles.wrapper, {
        [styles.isDrag]: control.drag,
      })}
    >
      <div className={styles.header}>
        <Search
          allowClear
          placeholder={formatMessage({ id: "general.search" })}
          onSearch={control.setFilter}
        />
        {control.me?.is_admin && (
          <Button onClick={control.showAddNewLocation} type="primary">
            {formatMessage({ id: "locations.add_new" })}
          </Button>
        )}
      </div>
      {control.me?.is_admin && control.controllerId && (
        <div className={styles.controllerId}>
          <label>{formatMessage({ id: "account.controllerId" })}</label>
          <TextFieldWithCopy guid={control.controllerId} style={{ maxWidth: 272 }} />
        </div>
      )}
      {!isPhone && (
        <div className={styles.table_head}>
          {headers
            .filter(item => control.isAdmin || !item.isAdmin)
            .map((header, i) => (<span key={i}>{header.title}</span>))
          }
        </div>
      )}
      <Reorder.Group
        axis="y"
        values={control.filteredLocations.map(loc => loc.Id)}
        onReorder={control.onEditLocationPosition}
        className={styles.table_body}
        as='div'
      >
        {control.loading && <Loader />}
        {!control.loading &&
          control.locations.length < 1 &&
          control.initialized && (
            <div className={styles.empty}>
              {formatMessage({ id: "locations.empty" })}
            </div>
          )}
        {control.filteredLocations.map((loc, index) =>
          <LocItem
            key={loc.Id}
            loc={loc}
            index={index}
            headers={headers
              .filter(item => control.isAdmin || !item.isAdmin)
              .map(i => i.title)
            }
            isPhone={isPhone}
            hasActive={control.hasActive}
            onDrag={control.onDrag}
          />
        )}
      </Reorder.Group>
    </Panel>
  );
});

Locations3.displayName = "Locations3";

export default WithLayout(Locations3);
